import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

class FbtBundle extends HTMLElement {
  #abortController = null;
  #mainQuantity = 1;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#syncMainQuantity();
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });

    this.#updateCompanionQuantityStates();
    this.#updateTotals();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @returns {Array<{variantId: string, quantity: number}>} */
  getSelectedCompanionItems() {
    return this.#getCompanionItems()
      .map((item) => ({
        variantId: String(item.dataset.variantId || ''),
        quantity: this.#getItemQuantity(item),
      }))
      .filter((item) => item.variantId);
  }

  /** @returns {string[]} */
  getSelectedCompanionVariants() {
    return this.getSelectedCompanionItems().map((item) => item.variantId);
  }

  #syncMainQuantity() {
    const form = this.closest('product-form-component');
    const input = form?.querySelector('quantity-selector-component input[name="quantity"]');
    if (input instanceof HTMLInputElement) {
      this.#mainQuantity = Math.max(1, Number(input.value) || 1);
    }
  }

  #onQuantityUpdate = (event) => {
    if (event.detail?.cartLine) return;

    const form = this.closest('product-form-component');
    const target = /** @type {Node | null} */ (event.target);
    if (!form || !target || !form.contains(target)) return;

    this.#mainQuantity = Math.max(1, Number(event.detail?.quantity) || 1);
    this.#updateTotals();
  };

  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.matches('[data-fbt-checkbox], [data-fbt-quantity]')) return;

    if (target.matches('[data-fbt-quantity]')) {
      const item = target.closest('[data-fbt-item]');
      if (item instanceof HTMLElement) {
        target.value = String(this.#getItemQuantity(item));
      }
    }

    this.#updateCompanionQuantityStates();
    this.#updateTotals();
  };

  #onInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-fbt-quantity]')) return;
    this.#updateTotals();
  };

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const toggle = target.closest('[data-fbt-details-toggle]');
    if (toggle instanceof HTMLButtonElement) {
      this.#toggleDetails(toggle);
      return;
    }

    const decrease = target.closest('[data-fbt-quantity-decrease]');
    const increase = target.closest('[data-fbt-quantity-increase]');
    if (!decrease && !increase) return;

    const item = target.closest('[data-fbt-item]');
    const input = item?.querySelector('[data-fbt-quantity]');
    if (!(input instanceof HTMLInputElement)) return;

    const step = Number(input.step) || 1;
    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    let value = Number(input.value) || min;

    value = decrease ? Math.max(min, value - step) : Math.min(max, value + step);
    input.value = String(value);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  #onVariantUpdate = (event) => {
    const productId = event.detail?.data?.newProduct?.id || event.detail?.data?.productId;
    if (productId && productId !== this.dataset.productId) return;

    const variant = event.detail?.resource;
    if (!variant) return;

    const price = Number(variant.price);
    const compareAt =
      variant.compare_at_price && Number(variant.compare_at_price) > price
        ? Number(variant.compare_at_price)
        : price;

    this.dataset.mainPrice = String(price);
    this.dataset.mainCompare = String(compareAt);

    const mainItem = this.querySelector('[data-fbt-role="main"]');
    if (mainItem instanceof HTMLElement) {
      mainItem.dataset.variantId = String(variant.id);
      mainItem.dataset.price = String(price);
      mainItem.dataset.compareAtPrice = String(compareAt);
    }

    const pricesEl = this.querySelector('[data-fbt-main-prices]');
    if (pricesEl instanceof HTMLElement) {
      pricesEl.innerHTML = this.#buildPriceHtml(price, compareAt);
    }

    this.#updateTotals();
  };

  #buildPriceHtml(price, compareAt) {
    const useCurrency = this.dataset.useCurrency === 'true';
    let html = `<span class="price-item--sale price">${this.#formatCents(price, useCurrency)}</span>`;
    if (compareAt > price) {
      html = `<span class="compare-at-price">${this.#formatCents(compareAt, useCurrency)}</span>` + html;
    }
    return html;
  }

  #formatCents(cents, withCurrency) {
    const moneyFormat = withCurrency
      ? this.dataset.moneyWithCurrencyFormat || this.dataset.moneyFormat || '{{amount}}'
      : this.dataset.moneyFormat || '{{amount}}';
    return formatMoney(cents, moneyFormat, this.dataset.currency || 'USD');
  }

  #toggleDetails(button) {
    const panel = this.querySelector(`#${CSS.escape(button.getAttribute('aria-controls') || '')}`);
    if (!(panel instanceof HTMLElement)) return;

    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!expanded));
    panel.hidden = expanded;
    panel.style.setProperty('--fbt-details-height', expanded ? '0px' : `${panel.scrollHeight}px`);

    const text = button.querySelector('[data-fbt-details-toggle-text]');
    if (text instanceof HTMLElement) {
      text.textContent = expanded
        ? this.dataset.detailsLabel || 'Product details'
        : this.dataset.detailsHideLabel || 'Hide details';
    }
  }

  /** @returns {HTMLElement[]} */
  #getCompanionItems() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[data-fbt-role="companion"]')).filter((item) => {
        if (!(item instanceof HTMLElement) || item.dataset.available !== 'true') return false;
        const cb = item.querySelector('[data-fbt-checkbox]');
        return cb instanceof HTMLInputElement && cb.checked;
      })
    );
  }

  /** @param {HTMLElement} item */
  #getItemQuantity(item) {
    const input = item.querySelector('[data-fbt-quantity]');
    if (!(input instanceof HTMLInputElement)) return 1;

    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    return Math.min(max, Math.max(min, Number(input.value) || min));
  }

  #updateCompanionQuantityStates() {
    for (const item of this.querySelectorAll('[data-fbt-role="companion"]')) {
      const cb = item.querySelector('[data-fbt-checkbox]');
      const qtyInput = item.querySelector('[data-fbt-quantity]');
      const qtyButtons = item.querySelectorAll('[data-fbt-quantity-decrease], [data-fbt-quantity-increase]');
      const enabled = cb instanceof HTMLInputElement && cb.checked && !cb.disabled;

      if (qtyInput instanceof HTMLInputElement) {
        qtyInput.disabled = !enabled;
      }

      for (const button of qtyButtons) {
        if (button instanceof HTMLButtonElement) {
          button.disabled = !enabled;
        }
      }
    }
  }

  #updateTotals() {
    const mainUnitPrice = Number(this.dataset.mainPrice || 0);
    const mainUnitCompare = Number(this.dataset.mainCompare || mainUnitPrice);
    let totalPrice = mainUnitPrice * this.#mainQuantity;
    let totalCompare = mainUnitCompare * this.#mainQuantity;

    for (const item of this.#getCompanionItems()) {
      const quantity = this.#getItemQuantity(item);
      totalPrice += Number(item.dataset.price || 0) * quantity;
      totalCompare += Number(item.dataset.compareAtPrice || item.dataset.price || 0) * quantity;
    }

    const priceEl = this.querySelector('[data-fbt-total-price]');
    const compareEl = this.querySelector('[data-fbt-total-compare]');
    const badge = this.querySelector('[data-fbt-discount-badge]');

    if (priceEl instanceof HTMLElement) {
      priceEl.textContent = this.#formatCents(totalPrice, this.dataset.useCurrency === 'true');
    }

    if (compareEl instanceof HTMLElement) {
      if (totalCompare > totalPrice) {
        compareEl.textContent = this.#formatCents(totalCompare, this.dataset.useCurrency === 'true');
        compareEl.classList.remove('hidden');
      } else {
        compareEl.textContent = '';
        compareEl.classList.add('hidden');
      }
    }

    if (badge instanceof HTMLElement) {
      if (totalCompare > totalPrice && totalCompare > 0) {
        const pct = Math.round(((totalCompare - totalPrice) / totalCompare) * 100);
        badge.textContent = (this.dataset.discountLabel || '-[discount]%').replace('[discount]', String(pct));
        badge.classList.remove('hidden');
        badge.removeAttribute('aria-hidden');
      } else {
        badge.textContent = '';
        badge.classList.add('hidden');
        badge.setAttribute('aria-hidden', 'true');
      }
    }
  }
}

if (!customElements.get('fbt-bundle')) {
  customElements.define('fbt-bundle', FbtBundle);
}
